import React, { FunctionComponent, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { GridApi } from 'ag-grid-community'
import { DataGridFiltersWrapper, FieldsWrapper, ToolbarToggleWrapper, FiltersSlideDown } from './filtersStyles'
import { TextInputField } from 'view/components/inputs'
import {
  TextButton,
  TextButtonType,
  ToolbarTextFlagButton,
  TextFlagButtonsContainer,
  getTextFlagButtonState
} from 'view/components/buttons'
import {
  FilterOperation,
  NegotiationHistorySubscriptionIdentifier,
  EntityTypeEnum,
  ValidationIssue,
  SubscriptionFilter,
  SubscriptionIdentifier,
  NegotiationSubscriptionIdentifier
} from 'api'
import { useChangedSubscriptionFilters } from 'view/shared/entity/hooks/useChangedSubscriptionFilters'
import { createEntitySubscriptionFiltersSelector } from 'selectors/selectionSelectors'
import { authorisedUserSelector } from 'selectors/connectionSelectors'
import { areOnlyDefaultSubscriptionFiltersActive } from 'model'
import {
  currentAndActiveSubscriptionFiltersAreEqual,
  getDateRangeSubscriptionFilters,
  getDateRangeSubscriptionFilterValue,
  getSubscriptionFilterValue,
  getUpdatedSubscriptionFilters
} from './utils'
import { Locale, Translation } from 'locale'
import { useSelector } from 'react-redux'
import { SubscriptionDateRangeComponent, SubscriptionDates } from './SubscriptionFilterComponents/SubscriptionDateRange'
import { isNotEmpty } from 'utils/predicate'
import { isEmpty } from 'lodash'

type NegotiationSubscriptionFiltersProps = StateProps & OwnProps

interface StateProps {
  activeSubscriptionFilters: SubscriptionFilter[]
}

interface OwnProps {
  entityType: EntityTypeEnum.NEGOTIATION | EntityTypeEnum.NEGOTIATION_HISTORY
  pageResults?: boolean
  subscriptionIdentifier: SubscriptionIdentifier
  gridApi?: GridApi
}

const entityTypeSelector = (_: {}, props: NegotiationSubscriptionFiltersProps) => props.entityType
const subscriptionIdentifierSelector = (_: {}, props: NegotiationSubscriptionFiltersProps) => props.subscriptionIdentifier

const mapStateToProps = createStructuredSelector({
  activeSubscriptionFilters: createEntitySubscriptionFiltersSelector(entityTypeSelector, subscriptionIdentifierSelector)
})

const negotiationSubscriptionFiltersFieldNames = ['createdAt', 'startDate', 'endDate', 'negotiationId', 'orderId']

export const NegotiationSubscriptionFiltersComponent: FunctionComponent<NegotiationSubscriptionFiltersProps> = ({
  activeSubscriptionFilters,
  entityType,
  subscriptionIdentifier,
  pageResults,
  gridApi
}) => {
  const authorisedUser = useSelector(authorisedUserSelector)
  const onChangeSubscription = useChangedSubscriptionFilters(entityType, subscriptionIdentifier, pageResults)
  const activeNegotiationSubscriptionFilters = activeSubscriptionFilters.filter(activeSubscriptionFilter =>
    negotiationSubscriptionFiltersFieldNames.includes(activeSubscriptionFilter.fieldName)
  )
  const isTodayFilterActive = areOnlyDefaultSubscriptionFiltersActive(subscriptionIdentifier, activeSubscriptionFilters, authorisedUser)
  const isNegotiationHistoryScreen = subscriptionIdentifier === NegotiationHistorySubscriptionIdentifier.LATEST
  const [subscriptionFilters, setSubscriptionFilters] = useState<SubscriptionFilter[]>(
    isTodayFilterActive ? [] : activeNegotiationSubscriptionFilters
  )
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([])
  const searchButtonDisabled =
    currentAndActiveSubscriptionFiltersAreEqual(subscriptionFilters, activeNegotiationSubscriptionFilters) ||
    isNotEmpty(validationIssues) ||
    isEmpty(subscriptionFilters)

  if (
    (entityType === EntityTypeEnum.NEGOTIATION && !(subscriptionIdentifier in NegotiationSubscriptionIdentifier)) ||
    (entityType === EntityTypeEnum.NEGOTIATION_HISTORY && !(subscriptionIdentifier in NegotiationHistorySubscriptionIdentifier))
  ) {
    throw new Error(`Invalid subscription identifier [${subscriptionIdentifier}] for entity type [${entityType}]`)
  }

  const handleSubscribeToday = () => {
    onChangeSubscription(null)
  }

  const handleUnsubscribe = () => {
    onChangeSubscription([])
  }

  const handleSubscribeHistorical = () => {
    gridApi?.collapseAll()
    onChangeSubscription(subscriptionFilters)
  }

  const handleUpdateFilterValue = (fieldName: string, filterOperation: FilterOperation) => (value: string | null) => {
    const updatedSubscriptionFilters = getUpdatedSubscriptionFilters(subscriptionFilters, fieldName, value, filterOperation)
    setSubscriptionFilters(updatedSubscriptionFilters)
  }

  const handleDateRangeUpdate = (fieldName: string, isDateAndTimeFilter = false) => (
    subscriptionDates: SubscriptionDates,
    validationIssues: ValidationIssue[]
  ) => {
    setValidationIssues(validationIssues)
    setSubscriptionFilters(getDateRangeSubscriptionFilters(subscriptionDates, fieldName, subscriptionFilters, isDateAndTimeFilter))
  }

  return (
    <DataGridFiltersWrapper>
      <ToolbarToggleWrapper>
        <div>{!isTodayFilterActive && Locale.translate(Translation.SC_FILTER_SEARCH_BY)}</div>
        <TextFlagButtonsContainer>
          <ToolbarTextFlagButton state={getTextFlagButtonState(isTodayFilterActive)} onClick={handleSubscribeToday}>
            {Locale.translate(Translation.TC_FILTER_TODAYS)}
          </ToolbarTextFlagButton>
          <ToolbarTextFlagButton state={getTextFlagButtonState(!isTodayFilterActive)} onClick={handleUnsubscribe}>
            {Locale.translate(Translation.TC_FILTER_SEARCH)}
          </ToolbarTextFlagButton>
        </TextFlagButtonsContainer>
      </ToolbarToggleWrapper>
      <FiltersSlideDown closed={isTodayFilterActive}>
        <div>
          <FieldsWrapper>
            <SubscriptionDateRangeComponent
              label={Translation.TC_FILTER_CREATED}
              onUpdate={handleDateRangeUpdate('createdAt', true)}
              initialSubscriptionDates={getDateRangeSubscriptionFilterValue(subscriptionFilters, 'createdAt')}
            />
          </FieldsWrapper>
          <FieldsWrapper>
            <SubscriptionDateRangeComponent
              label={Translation.TC_START_DATE}
              onUpdate={handleDateRangeUpdate('startDate')}
              initialSubscriptionDates={getDateRangeSubscriptionFilterValue(subscriptionFilters, 'startDate')}
            />
          </FieldsWrapper>
          <FieldsWrapper>
            <SubscriptionDateRangeComponent
              label={Translation.TC_END_DATE}
              onUpdate={handleDateRangeUpdate('endDate')}
              initialSubscriptionDates={getDateRangeSubscriptionFilterValue(subscriptionFilters, 'endDate')}
            />
          </FieldsWrapper>
          {isNegotiationHistoryScreen ? (
            <FieldsWrapper>
              <label>{Locale.translate(Translation.TC_NEGOTIATION_ID)}</label>
              <TextInputField
                label=""
                value={getSubscriptionFilterValue(subscriptionFilters, 'negotiationId')}
                onUpdate={handleUpdateFilterValue('negotiationId', FilterOperation.EQUALS)}
                dataTestId={'NegotiationSubscriptionFilters-TextInput-NegotiationIdEquals'}
                autoCapitalise={true}
              />
              <label>{Locale.translate(Translation.TC_RFQ_ID)}</label>
              <TextInputField
                label=""
                value={getSubscriptionFilterValue(subscriptionFilters, 'orderId')}
                onUpdate={handleUpdateFilterValue('orderId', FilterOperation.EQUALS)}
                dataTestId={'NegotiationSubscriptionFilters-TextInput-RfqIdEquals'}
                autoCapitalise={true}
              />
            </FieldsWrapper>
          ) : null}
        </div>
        <TextButton type={TextButtonType.Primary} disabled={searchButtonDisabled} onClick={handleSubscribeHistorical}>
          {Locale.translate(Translation.TC_BUTTON_SEARCH)}
        </TextButton>
      </FiltersSlideDown>
    </DataGridFiltersWrapper>
  )
}

export const NegotiationSubscriptionFilters = connect<StateProps, null, OwnProps>(mapStateToProps)(NegotiationSubscriptionFiltersComponent)
